import { Link } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import {
  Building2,
  Globe,
  FlaskConical,
  Target,
  Users,
  ArrowUpRight,
} from "lucide-react";
import type { ContactDetailFull } from "@/hooks/useContactDetail";

interface ContactContextProps {
  contact: ContactDetailFull;
}

export function ContactContext({ contact }: ContactContextProps) {
  const hasCompanyData =
    contact.company_name || contact.company_primary_category || contact.company_icp_segment;

  if (!hasCompanyData) return null;

  return (
    <div className="mx-6 mt-3 mb-1 rounded-lg border bg-muted/30 p-3 space-y-2">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-xs font-medium text-muted-foreground uppercase tracking-wide">
          <Building2 className="h-3.5 w-3.5" />
          Account Context
        </div>
        {contact.company_id && (
          <Link
            to={`/accounts/${contact.company_id}`}
            className="text-[11px] text-primary hover:underline inline-flex items-center gap-0.5"
            onClick={(e) => e.stopPropagation()}
          >
            Full Account <ArrowUpRight className="h-3 w-3" />
          </Link>
        )}
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-2 text-sm">
        {contact.company_name && (
          <div>
            <span className="text-xs text-muted-foreground">Company</span>
            {contact.company_id ? (
              <Link
                to={`/accounts/${contact.company_id}`}
                className="font-medium truncate text-primary hover:underline block"
                onClick={(e) => e.stopPropagation()}
              >
                {contact.company_name}
              </Link>
            ) : (
              <div className="font-medium truncate">{contact.company_name}</div>
            )}
          </div>
        )}
        {contact.company_domain && (
          <div>
            <span className="text-xs text-muted-foreground">Domain</span>
            <div className="font-medium truncate flex items-center gap-1">
              <Globe className="h-3 w-3 text-muted-foreground" />
              {contact.company_domain}
            </div>
          </div>
        )}
        {contact.company_primary_category && (
          <div>
            <span className="text-xs text-muted-foreground">Category</span>
            <div className="font-medium truncate flex items-center gap-1">
              <FlaskConical className="h-3 w-3 text-muted-foreground" />
              {contact.company_primary_category}
            </div>
          </div>
        )}
        {contact.company_icp_segment && (
          <div>
            <span className="text-xs text-muted-foreground">ICP Segment</span>
            <div className="font-medium truncate flex items-center gap-1">
              <Target className="h-3 w-3 text-muted-foreground" />
              {contact.company_icp_segment}
            </div>
          </div>
        )}
        {contact.company_icp_tier && (
          <div>
            <span className="text-xs text-muted-foreground">ICP Tier</span>
            <Badge variant="outline" className="text-xs mt-0.5">
              {contact.company_icp_tier}
            </Badge>
          </div>
        )}
        {contact.company_employee_count && (
          <div>
            <span className="text-xs text-muted-foreground">Employees</span>
            <div className="font-medium flex items-center gap-1">
              <Users className="h-3 w-3 text-muted-foreground" />
              {contact.company_employee_count.toLocaleString()}
            </div>
          </div>
        )}
      </div>

      {contact.company_description && (
        <p className="text-xs text-muted-foreground line-clamp-2 pt-1">
          {contact.company_description}
        </p>
      )}
    </div>
  );
}
